import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult } from '../../interfaces';
import { copyETLDryRunObjectTableColumns, ETLDryRunObjectTableAddColumn } from '../utils';
import { addDryRunObjectTable, getETLComponentInputObjectTable } from '../../utils';
import { get_guid } from '../../../visual-analytics/helpers/misc';

import styles from '../../edit-etl-component/editETLComponentForm.module.scss';
import ParamSet from '../../edit-etl-component/ParamSet';
import { VALTYPES } from '../../../../data/interfaces';
import { addETLDryRunError, getDryRunErrorsForComponent } from '../../etlDryRun';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import { binaryOperators, parseETLTableExpression } from './etlExpressionParser';
import Expander from '../../../../components/expander/Expander';
import { currentTheme } from '../../../themes/themes';
import { etlExpressionFunctionList } from './etlExpressionFunctions';

interface TpComputationInfo {
  id: string;
  expression: string;
  parsedExpressionTree?: any;
  columnIdentifier: string;
  columnName: string;
  columnDescription: string;
  columnDataType: string;
}

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const inputTableObj = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');
  const outputTableObj = addDryRunObjectTable(dryRun, comp.outputObjectIds['result'], comp);
  copyETLDryRunObjectTableColumns(inputTableObj, outputTableObj);

  const computations = (comp.params['computations'] || []) as TpComputationInfo[];
  for (const computation of computations) {
    try {
      computation.parsedExpressionTree = null;
      if (!computation.expression) throw `Expression should not be empty`;
      ETLDryRunObjectTableAddColumn(
        outputTableObj,
        computation.columnIdentifier,
        computation.columnName,
        computation.columnDescription,
        computation.columnDataType,
      );
      computation.parsedExpressionTree = parseETLTableExpression(inputTableObj, computation.expression);
    } catch (error) {
      addETLDryRunError(dryRun, String(error), comp.id, computation.id);
    }
  }

  return {
    abort: false,
  };
}

function renderCustomConfigParams(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
): any {
  const sourceTable = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');

  const computations = (comp.params['computations'] || []) as TpComputationInfo[];

  function updateComputationParam(computationId: string, paramId: string, newValue: any) {
    const computationIdx = computations.map((computation) => computation.id).indexOf(computationId);
    if (computationIdx < 0) throw `Invalid computation id ${computationId}`;
    else if (paramId == 'expression') computations[computationIdx].expression = newValue;
    else if (paramId == 'columnIdentifier') computations[computationIdx].columnIdentifier = newValue;
    else if (paramId == 'columnName') computations[computationIdx].columnName = newValue;
    else if (paramId == 'columnDescription') computations[computationIdx].columnDescription = newValue;
    else if (paramId == 'columnDataType') computations[computationIdx].columnDataType = newValue;
    updateEditingComponent(comp);
  }

  function renderComputationErrors(computation: TpComputationInfo) {
    const errors = getDryRunErrorsForComponent(dryRun, comp.id).filter((error) => error.paramId == computation.id);
    if (errors.length == 0) return null;
    return (
      <div className={styles.errors}>
        {errors.map((error, idx) => (
          <div key={`errors_${computation.id}_${idx}`}>{error.message}</div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <Expander collapsedMaxHeight={110} backgroundColor={currentTheme.colors.background}>
        <div>
          Create one or more new columns by computing an expression over existing columns.
          <br />
          Available operators: {binaryOperators.join(', ')}
          , [condition] ? [consequent] : [alternate]
          <br />
          Available functions:
          {etlExpressionFunctionList.map((fnc) => (
            <p key={fnc.name}>
              <b>{fnc.signature}</b>
              <br />
              {fnc.description}
            </p>
          ))}
        </div>
      </Expander>
      {computations.map((computation) => (
        <div className={styles.section} key={computation.id}>
          <h3>
            Computation
            <button
              onClick={() => {
                comp.params['computations'] = computations.filter(
                  (filtComputation) => computation.id !== filtComputation.id,
                );
                updateEditingComponent(comp);
              }}
            >
              <FontAwesomeIcon icon="xmark" />
            </button>
          </h3>
          <ParamSet
            params={[
              {
                name: 'Expression',
                component: (
                  <textarea
                    value={computation.expression}
                    onChange={(ev) => {
                      updateComputationParam(computation.id, 'expression', ev.target.value);
                    }}
                  />
                ),
              },
              {
                name: 'Column Data type',
                component: (
                  <select
                    value={computation.columnDataType}
                    onChange={(ev) => {
                      updateComputationParam(computation.id, 'columnDataType', ev.target.value);
                    }}
                  >
                    {Object.entries(VALTYPES).map(([k, v]) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                ),
              },
              {
                name: 'Column Identifier',
                component: (
                  <input
                    value={computation.columnIdentifier}
                    onChange={(ev) => {
                      updateComputationParam(computation.id, 'columnIdentifier', ev.target.value);
                    }}
                  />
                ),
              },
              {
                name: 'Column Name',
                component: (
                  <input
                    value={computation.columnName}
                    onChange={(ev) => {
                      updateComputationParam(computation.id, 'columnName', ev.target.value);
                    }}
                  />
                ),
              },
              {
                name: 'Column Description',
                component: (
                  <input
                    value={computation.columnDescription}
                    onChange={(ev) => {
                      updateComputationParam(computation.id, 'columnDescription', ev.target.value);
                    }}
                  />
                ),
              },
            ]}
          />
          {renderComputationErrors(computation)}
        </div>
      ))}

      <FancyButton
        iconName="fas:plus"
        onClick={() => {
          const newComputation: TpComputationInfo = {
            id: get_guid(),
            expression: '',
            columnIdentifier: '',
            columnName: '',
            columnDescription: '',
            columnDataType: VALTYPES.STRING,
          };
          comp.params['computations'] = [...computations, newComputation];
          updateEditingComponent(comp);
        }}
      >
        Add computation
      </FancyButton>
    </div>
  );
}

export const manifestETLApplyExpressionTable: TpETLComponentTypeManifest = {
  id: 'ApplyExpressionTable',
  name: 'Compute column',
  description: 'Computes one or more new columns, using an expression over the existing columns',
  categoryId: 'table',
  icon: <FontAwesomeIcon icon={['fas', 'function']} />,
  inputs: [
    {
      type: TpETLObjectTypes.TABLE,
      id: 'sourceTable',
      name: 'Source Table',
      columns: [],
    },
  ],
  outputs: [
    {
      id: 'result',
      name: 'Result table',
      type: TpETLObjectTypes.TABLE,
      description: 'Table with the filtered rows',
      columns: [],
    },
  ],
  params: [],
  performDryRun,
  renderCustomConfigParams,
};
